import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ReasonModal } from "@/components/ReasonModal";
import {
  PRIORITY_TIER_SORT_ORDER,
  PRIORITY_TIER_LABELS,
  PRIORITY_URGENT_CATEGORIES,
  PRIORITY_TIER_CATEGORIES,
  priorityBadgeVariant,
  getPriorityTier,
} from "@/lib/priorityTier";
import { api } from "@/lib/api";

/**
 * Small inline badge — renders "—" for unassigned records so cards stay
 * quiet. Use in list rows and card headers.
 */
export const PriorityBadge = ({ tier, size = "sm", testid = "priority-badge" }) => {
  const v = priorityBadgeVariant(tier);
  const sizing = size === "lg" ? "text-[11px] px-2 py-0.5" : "text-[10px] px-1.5 py-0.5";
  return (
    <span
      className={`inline-flex items-center rounded-md border ${sizing} tracking-[0.14em] uppercase ${v.className}`}
      title={v.title}
      data-testid={testid}
      data-priority-tier={(tier || "").toString().toUpperCase() || "NONE"}
    >
      {v.label}
    </span>
  );
};

/**
 * Full priority control — a row of tier buttons that opens the ReasonModal
 * for the selected tier and posts to the given endpoint. Callers pass a
 * record + endpoint. The `onChanged` callback receives the API response
 * so parents can refresh.
 *
 * Props:
 *   record         — the source record (reads .priority_tier)
 *   endpoint       — POST endpoint (e.g. `/tracks/${id}/priority`)
 *   onChanged(res) — invoked after successful save
 *   canEdit        — bool, disables all buttons when false
 *   compact        — bool, shrinks the tier buttons for card headers
 *   testidPrefix   — override the data-testid prefix (default 'priority-tier')
 */
export const PriorityTierControl = ({
  record,
  endpoint,
  onChanged,
  canEdit = true,
  compact = false,
  testidPrefix = "priority-tier",
}) => {
  const [target, setTarget] = useState(null); // "URGENT"|"P1"|"P2"|"P3"|"P4"|""
  const [busy, setBusy] = useState(false);
  const current = getPriorityTier(record);

  async function submit({ category, detail }) {
    if (target === null) return;
    setBusy(true);
    try {
      const r = await api.post(endpoint, { target_tier: target, category, detail });
      toast.success(`Priority tier updated${target ? ` → ${target}` : " → Unassigned"}.`);
      setTarget(null);
      if (onChanged) onChanged(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Update failed";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  const isUrgent = target === "URGENT";
  const categoryOptions = isUrgent ? PRIORITY_URGENT_CATEGORIES : PRIORITY_TIER_CATEGORIES;
  const btnCls = compact ? "px-2 py-1 h-7 text-[11px]" : "px-3 h-8 text-xs";

  const btn = (t) => {
    const active = current === t;
    const v = priorityBadgeVariant(t);
    return (
      <Button
        key={t || "none"}
        type="button"
        size="sm"
        variant={active ? "default" : "outline"}
        disabled={!canEdit}
        onClick={() => setTarget(t)}
        className={btnCls}
        data-testid={`${testidPrefix}-${t || "none"}`}
        style={active ? { background: "hsl(347 72% 22%)" } : undefined}
        title={v.title}
      >
        {t || "Clear"}
      </Button>
    );
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testidPrefix}-control`}>
        <PriorityBadge tier={current} size="lg" testid={`${testidPrefix}-current-badge`} />
        {canEdit && (
          <>
            {PRIORITY_TIER_SORT_ORDER.map(btn)}
            {btn("")}
          </>
        )}
      </div>

      <ReasonModal
        open={target !== null}
        onOpenChange={(v) => { if (!v) setTarget(null); }}
        title={isUrgent
          ? "URGENT — Compliance / Legal Risk Override"
          : target === ""
            ? "Clear Priority Tier"
            : `Set Priority Tier ${target || ""}`}
        description={isUrgent
          ? "Assign the URGENT override only for documented compliance / legal risk (takedown threat, ownership dispute, release-live errors, attorney deadline, payment dispute). Reason is required."
          : target === ""
            ? "Clear the priority tier. Historical audit record is preserved. Reason is required."
            : `Assign ${target ? PRIORITY_TIER_LABELS[target] || target : ""}. Priority Tier does NOT bypass Asset Pathway, clearance, or lifecycle gating. Reason is required.`}
        categoryOptions={categoryOptions}
        confirmLabel={target === "" ? "Clear Priority" : `Set ${target}`}
        severity={isUrgent ? "critical" : "high"}
        busy={busy}
        testid={`${testidPrefix}-modal`}
        onConfirm={submit}
      />
    </>
  );
};

export default PriorityTierControl;
